import { ChevronLeft, ChevronRight, Eye, History } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  formatDateWIB,
  formatEntityName,
  getActionMeta,
} from "./activity-log-utils";

import type { ActivityLog } from "@artisancode/api-types";

interface ActivityLogTableProps {
  items: ActivityLog[];
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  total: number;
  lastPage: number;
  openDetail: (log: ActivityLog) => void;
}

export function ActivityLogTable({
  items,
  isLoading,
  page,
  setPage,
  total,
  lastPage,
  openDetail,
}: ActivityLogTableProps) {
  return (
    <CardContent className="p-0">
      <div className="border-t">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Waktu</TableHead>
              <TableHead>Pengguna</TableHead>
              <TableHead>Modul</TableHead>
              <TableHead className="w-[100px]">Aksi</TableHead>
              <TableHead>ID Entitas</TableHead>
              <TableHead>Perubahan</TableHead>
              <TableHead className="w-[80px] text-right">Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-28" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-36" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="h-8 w-8 ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="h-48 text-center text-muted-foreground"
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <History className="h-8 w-8 text-muted-foreground/60" />
                    <p className="text-sm font-medium">
                      Belum ada riwayat aktivitas yang tercatat.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Setiap penambahan, pengubahan, atau penghapusan data akan
                      otomatis tercatat di sini.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              items.map((log) => {
                const actionMeta = getActionMeta(log.activity);
                const diffCount = log.diff ? Object.keys(log.diff).length : 0;

                return (
                  <TableRow key={log.id} className="hover:bg-muted/40">
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDateWIB(log.createdAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-xs text-foreground">
                          {log.userName || "System"}
                        </span>
                        {log.userEmail && (
                          <span className="text-[11px] text-muted-foreground">
                            {log.userEmail}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">
                        {formatEntityName(log.entityName)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={actionMeta.badgeClass}
                      >
                        {actionMeta.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-[11px] font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                        {log.entityId.length > 12
                          ? `${log.entityId.slice(0, 8)}...`
                          : log.entityId}
                      </code>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {log.activity === "create" ? (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          Data dibuat
                        </span>
                      ) : log.activity === "delete" ? (
                        <span className="text-rose-600 dark:text-rose-400">
                          Data dihapus
                        </span>
                      ) : diffCount > 0 ? (
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          {diffCount} kolom diubah
                        </span>
                      ) : (
                        <span>Diperbarui</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => openDetail(log)}
                        title="Lihat Detail Perubahan"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Footer */}
      {total > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t text-xs text-muted-foreground">
          <span>
            Total <strong>{total}</strong> aktivitas (Halaman {page} dari{" "}
            {lastPage})
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="px-2 font-medium text-foreground">{page}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={page >= lastPage}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </CardContent>
  );
}
